import { css } from '@emotion/react'
import {
  ColorPrimitives,
  CornerRadius,
  FontWeight,
  gemVar,
  LineWeight,
  Spacing,
  ThemedColors,
  ThemedFocusIndicator,
  Typography,
} from '@opallabs/gem'

const rootClass = 'opal-calendar-filter-button'
export const selectors = {
  root: rootClass,
  counter: `${rootClass}-counter`,
  label: `${rootClass}-label`,
} as const
export const states = {
  /** The drawer is open, or a filter is applied: the button is doing something. */
  active: 'active',
} as const

/** Any radius past half the height draws a round-ended pill. */
const PILL_RADIUS = 999

/**
 * The Filter button of the Web Components file (3354:11342): a 32px ghost
 * button that takes a tinted face and a hairline on hover, holds them while it
 * is active, and counts the applied filters in a blue pill after its word.
 */
export const root = css({
  ...Typography.T500,
  alignItems: 'center',
  backgroundColor: 'transparent',
  blockSize: Spacing.XS * 8,
  border: `${LineWeight.Small}px solid transparent`,
  borderRadius: CornerRadius.Small,
  boxSizing: 'border-box',
  color: gemVar(ThemedColors.TextPrimary),
  cursor: 'pointer',
  display: 'flex',
  paddingInline: Spacing.S,

  '&:hover': {
    backgroundColor: gemVar(ThemedColors.Primary2),
    borderColor: gemVar(ThemedColors.StrokePrimary1),
  },
  [`&.${states.active}`]: {
    backgroundColor: gemVar(ThemedColors.Primary3),
    borderColor: gemVar(ThemedColors.StrokePrimary1),
  },
  '&:focus-visible': ThemedFocusIndicator,

  // The design pads the word 5px after the icon and 4px before the counter.
  [`.${selectors.label}`]: {
    paddingInlineEnd: Spacing.XS,
    paddingInlineStart: Spacing.XS + 1,
  },
  // A 16px pill that is a circle round one digit and hugs a longer count.
  [`.${selectors.counter}`]: {
    ...Typography.TL300,
    // The design sets the count in Medium; Gem's 12px style is Semibold.
    fontWeight: FontWeight.Medium,
    alignItems: 'center',
    backgroundColor: gemVar(ColorPrimitives.B450),
    blockSize: Spacing.M,
    // The label style trims its line to the letters, but not inside a flex
    // box, where the text is its own item. The line is the pill's height
    // instead, so the digit sits in the middle of it.
    lineHeight: `${Spacing.M}px`,
    borderRadius: PILL_RADIUS,
    boxSizing: 'border-box',
    // The pill is the same blue in both themes, so its digit is the same
    // white: the themed contrast text is near-black in the dark theme.
    color: gemVar(ColorPrimitives.N000),
    display: 'inline-flex',
    justifyContent: 'center',
    marginInlineStart: Spacing.XS,
    minInlineSize: Spacing.M,
    paddingInline: Spacing.XS,
  },
})
